// A sales window that closes before it opens is never satisfiable, so the
// ticket type or add-on never goes on sale and never reaches checkout. Nothing
// downstream reports it, which is why the row has to say so (O-4840).
//
// Tiered availabilities are excluded: for those, TicketType#start_time returns
// a sentinel (100 years out until the tier triggers, then 5 minutes ago), so
// comparing it to end_time says nothing about a sales window.
//
// Equal timestamps are a valid window (the model allows start <= end), so the
// comparison is strict.
export const salesWindowInverted = (sellable) => {
  if (!sellable) return false;
  if (sellable.wait_for_ticket_type && sellable.wait_for_ticket_type.id) return false;
  if (!sellable.start_time || !sellable.end_time) return false;

  return new Date(sellable.start_time) > new Date(sellable.end_time);
};

// Shared by both show-detail editors so the wording can't drift between them.
export const INVERTED_WINDOW_WARNING =
  "Tickets using this show detail will never go on sale — its sales window closes before it opens. Move this date or its paired one.";

// The Ticketing tab says it about the row itself rather than a show detail,
// since the badge there can fire for a window that uses no show details at all.
export const INVERTED_WINDOW_TICKET_WARNING =
  "This ticket's sales window closes before it opens, so it will never go on sale.";

export const INVERTED_WINDOW_ADD_ON_WARNING =
  "This add-on's sales window closes before it opens, so it will never go on sale.";

// The Ticketing tab's scan-window twins. These say the window is not enforced,
// not that it locks people out — an unusable window means the door stops
// applying timed entry, which is an access gap rather than a denial. Unlike
// salesWindowInverted these are
// not computed here: for a show-detail-based window the raw timed_entry_*
// columns are meaningless, the real bounds live on two show activities, and
// nothing puts those times in this payload. The server sends the verdict as
// timed_entry_window_unusable instead (O-4861).
export const TIMED_ENTRY_UNUSABLE_TICKET_WARNING =
  "This ticket's timed entry window is unusable, so timed entry isn't enforced — it will scan in at any time.";

export const TIMED_ENTRY_UNUSABLE_ADD_ON_WARNING =
  "This add-on's timed entry window is unusable, so timed entry isn't enforced — it will scan in at any time.";

// The show-detail twin. Its consequence is the opposite of the sales-window
// string above: an inverted sales window stops tickets selling, while an
// unusable scan window sells fine and then quietly stops being enforced at the
// door — so this is the only notice anyone gets before the show (O-4861).
export const TIMED_ENTRY_UNUSABLE_WINDOW_WARNING =
  "Timed entry isn't being enforced for tickets using this show detail — they'll scan in at any time. Its scan window closes at or before it opens, or is missing a date. Check this date and its paired one.";

// A scan bound with no time set. Deliberately says "partly" rather than "not at
// all": the door enforces each bound on its own, so if the paired date is live
// the other half still applies — a fan can still be turned away for arriving
// early even though nothing closes the window.
export const UNSET_TIMED_ENTRY_WARNING =
  "This date is being used as a timed entry scan window. Until it has a date and time, that half of the window isn't enforced.";

// Doors belong at or before the event starts. Unlike the sales window, both
// times are already in the editor's activity list, so there is nothing the
// server knows that the client doesn't and no reason to serialize a fourth
// boolean for it. Like the others it renders on the settled rows, not on the
// row currently open for editing.
export const DOORS_AFTER_START_WARNING =
  "Doors are after the event starts. Move Doors to on or before Event Start.";

// Earliest match rather than first, because the name is not reliably unique.
// ShowActivity only validates uniqueness for names in ShowActivity.required,
// which is empty for any mode outside venue and agent manager — so an
// attractions team can hold two "Doors" rows and array order would decide which
// one the warning judged. Earliest is the one an operator would be let in by.
const activityTime = (activities, name) => {
  const times = (activities || [])
    .filter((a) => a.name === name && a.set && a.start_time)
    .map((a) => new Date(a.start_time));

  return times.length ? new Date(Math.min(...times)) : null;
};

export const doorsAfterStart = (activities, activity) => {
  if (!activity || !["Doors", "Event Start"].includes(activity.name)) return false;

  const doors = activityTime(activities, "Doors");
  const start = activityTime(activities, "Event Start");
  if (!doors || !start) return false;

  return doors > start;
};

// Reads the serialized ShowActivity#internal, never the row's name. The name
// alone is not enough: on a team mode where "Publish" isn't a required activity
// it's just a custom row someone called that, and treating it as internal would
// take away its eye toggle and its edit control for no reason.
//
// Internal rows are always show_in_advance: false on the server, and both
// editors filter on show_in_advance with "Visible" as the default view — so
// without an exemption they'd be invisible in the one list that is supposed to
// show every date and time. They're exempt from the filter and its counts.
export const isInternalActivity = (activity) => !!activity && activity.internal === true;

// Unset means the event has never been published. The first publish runs through
// Confirm > Publish, which is where the ticketing prerequisites are enforced, so
// the row is shown but not editable until then. The server enforces this too —
// this only keeps the UI from offering an edit that would be refused.
export const publishNotYetSettable = (activity) =>
  isInternalActivity(activity) && !activity.set;

export const PUBLISH_PENDING_TOOLTIP =
  "Set up ticketing to set the publish time for the first time";
